import { useMemo } from 'react';
import { Radio } from 'antd';

import { PROCESS_STAGE_LIST_ERRORS, columnDefsDocument } from './constants';

import { manufacturingProcessCharactersLimit } from '../../constant/charactersLimit';

import { DocumentActions } from '../qa-grid-cell';
import CellActionRenderSnapshotWrapper from '../qa-grid-cell/CellActionRenderSnapshotWrapper';
import RadioBooleanSnapshot from 'pages/qa-spec/components/qa-grid-cell/RadioBooleanSnapshot';
import { QaSpecHelpContentGridLabel } from 'pages/qa-spec/components/shared/QaSpecHelpContent';
import { useCheckDisableDownloadAsset } from 'hooks/useCheckDisableDownload';

const validateEmptyRows = (row) => {
  if (!row.processStage?.trim()) {
    return [PROCESS_STAGE_LIST_ERRORS.PROCESS_STAGE_EMPTY];
  } else {
    return [];
  }
};

const validateQasRequiredFields = (row, requiredFields, displayNameMaps) => {
  let errors = [];

  Object.keys(row).forEach((fieldName) => {
    if (fieldName === 'processStage') return; //*  'processStage' has validated in validateEmptyRows
    if (!requiredFields?.length) return;

    const isNormalFieldRequired = requiredFields.includes(
      `qaSpecification.qaSpecManufacturingProcesses.${fieldName}`.toLowerCase()
    );

    const value = row[fieldName];
    const isInvalid = isNormalFieldRequired && !value?.length;
    const displayName = displayNameMaps[fieldName];
    const errorMsg = (displayName || fieldName) + ' is required';

    if (isInvalid) {
      errors = [...new Set([...errors, errorMsg])];
    }
  });

  return errors;
};

export const validateRowData = (rowData, requiredFields, displayNameMaps) => {
  return rowData.map((row) => {
    let errors;

    errors = validateEmptyRows(row);

    if (requiredFields?.length) {
      const qasRequiredErrors = validateQasRequiredFields(
        row,
        requiredFields,
        displayNameMaps
      );
      errors = [...new Set([...errors, ...qasRequiredErrors])];
    }

    return {
      ...row,
      errors,
    };
  });
};

export const getAllQasRequiredFieldsError = (
  requiredFields,
  displayNameMaps
) => {
  if (!displayNameMaps) return [];

  return Object.keys(displayNameMaps)
    .map((fieldName) => {
      if (fieldName === 'processStage') return null; //*  'processStage' has validated in validateEmptyRows
      if (!requiredFields?.length) return null;

      const isNormalFieldRequired = requiredFields.includes(
        `qaSpecification.qaSpecManufacturingProcesses.${fieldName}`.toLowerCase()
      );

      const displayName = displayNameMaps[fieldName];
      const errorMsg = (displayName || fieldName) + ' is required';

      if (isNormalFieldRequired) {
        return errorMsg;
      }

      return null;
    })
    .filter(Boolean);
};

export const checkErrorInRowData = (rowData) => {
  return rowData.some((row) => row?.errors?.length > 0);
};

export const generateColumDefs = ({
  processStageCols,
  productEnums,
  isToggleSnapshot,
}) => {
  const statusField = {
    minWidth: 50,
    maxWidth: 50,
    field: 'errors',
    displayName: ' ',
    cellRenderer: 'StatusRender',
    type: 'nonEditableColumn',
  };

  let result = [statusField];

  processStageCols.forEach((col) => {
    if (col.field === 'processStage') {
      const newCol = {
        ...col,
        cellEditorSelector: (params) => {
          return {
            component: 'TextEditor',
            popup: true,
            params: {
              maxLength: manufacturingProcessCharactersLimit.processStage,
            },
          };
        },
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponentParams: {
          fieldPath: 'qaSpecification.manufacturingProcess.stages.processStage',
        },
      };
      result.push(newCol);
    } else if (col.field === 'natureOfTheTests') {
      const newCol = {
        ...col,
        cellEditorSelector: (params) => {
          return {
            component: 'TextEditor',
            popup: true,
            params: {
              maxLength: manufacturingProcessCharactersLimit.natureOfTheTests,
            },
          };
        },
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponentParams: {
          fieldPath:
            'qaSpecification.manufacturingProcess.stages.natureOfTheTests',
        },
      };
      result.push(newCol);
    } else if (col.field === 'targetValueAndTolerances') {
      const newCol = {
        ...col,
        cellEditorSelector: (params) => {
          return {
            component: 'TextEditor',
            popup: true,
            params: {
              maxLength:
                manufacturingProcessCharactersLimit.targetValueAndTolerances,
            },
          };
        },
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponentParams: {
          fieldPath:
            'qaSpecification.manufacturingProcess.stages.targetValueAndTolerances',
        },
      };
      result.push(newCol);
    } else if (col.field === 'frequency') {
      const newCol = {
        ...col,
        cellEditorSelector: (params) => {
          return {
            component: 'TextEditor',
            popup: true,
            params: {
              maxLength: manufacturingProcessCharactersLimit.frequency,
            },
          };
        },
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponentParams: {
          fieldPath: 'qaSpecification.manufacturingProcess.stages.frequency',
        },
      };
      result.push(newCol);
    } else if (col.field === 'test') {
      const newCol = {
        ...col,
        width: 300,
        flex: null,
        cellEditorSelector: () => {
          const options = productEnums?.find(
            (enumItem) => enumItem.enumName === 'ProcessTestCodeEnum'
          )?.enumProperties;

          return {
            popup: true,
            component: 'SelectionFreeText',
            params: {
              values: options.map((option) => ({
                value: option.enumDisplayName,
                displayName: option.enumDisplayName,
              })),
              overlayClassName: 'process-stage__dropdown',
              popup: true,
              maxLength: manufacturingProcessCharactersLimit.test,
            },
          };
        },
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponentParams: {
          fieldPath: 'qaSpecification.manufacturingProcess.stages.test',
        },
      };
      result.push(newCol);
    } else if (col.field === 'criticalPointEstablishedInHACCP') {
      const newCol = {
        ...col,
        cellEditorSelector: (params) => {
          return {
            component: 'TextEditor',
            popup: true,
            params: {
              maxLength: manufacturingProcessCharactersLimit.frequency,
            },
          };
        },
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponentParams: {
          fieldPath:
            'qaSpecification.manufacturingProcess.stages.criticalPointEstablishedInHaccp',
        },
      };
      result.push(newCol);
    } else if (col.field === 'recognizedMethods') {
      const newCol = {
        ...col,
        cellEditorSelector: () => {
          const options = productEnums?.find(
            (enumItem) => enumItem.enumName === 'RecognizedMethodsCodeEnum'
          )?.enumProperties;

          return {
            component: 'SelectionEditor',
            params: {
              values: options.map((optionItem) => ({
                value: optionItem.enumDisplayName,
                displayName:
                  optionItem.enumDisplayName === null
                    ? ''
                    : optionItem.enumDisplayName,
              })),
              mapValue: (value) => {
                if (value === 'null' || value === undefined) {
                  return null;
                } else {
                  return value;
                }
              },
            },

            popup: true,
          };
        },
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponentParams: {
          fieldPath:
            'qaSpecification.manufacturingProcess.stages.recognizedMethods',
        },
      };
      result.push(newCol);
    } else {
      result.push({
        ...col,
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
      });
    }
  });

  return result.map((fieldItem) => {
    const DEFAULT_WIDTH = 150;

    let initColSetting = {
      type: 'editableColumn',
    };

    return {
      ...initColSetting,
      ...fieldItem,
      minWidth: DEFAULT_WIDTH,
      headerComponent:
        fieldItem?.field === 'errors' ? null : QaSpecHelpContentGridLabel,
    };
  });
};

export const useGenerateColumDefsDocument = ({
  handlePreview,
  handleDownload,
  handleDelete,
  isToggleSnapshot,
}) => {
  const { checkDisableDownloadAssetDetail } = useCheckDisableDownloadAsset();

  const columnDefs = useMemo(() => {
    let result = [];

    columnDefsDocument.forEach((col) => {
      if (col.field === 'documentActions') {
        const newCol = {
          ...col,
          cellRenderer: (params) => {
            const linkUrl = params?.data?.linkUrl;
            const linkUrlSnapshot =
              params?.context?.snapshotGridValues?.[params?.rowIndex]?.linkUrl;

            const disableDownloadDocument = checkDisableDownloadAssetDetail(
              params?.data
            );
            return (
              <>
                {isToggleSnapshot ? (
                  <CellActionRenderSnapshotWrapper
                    {...params}
                    isHighlightChange={linkUrl !== linkUrlSnapshot}
                  >
                    <DocumentActions
                      disableDownload={disableDownloadDocument}
                      onPreview={() => handlePreview(linkUrlSnapshot)}
                      onDownload={() => handleDownload(linkUrlSnapshot)}
                      onDelete={() => handleDelete(linkUrlSnapshot)}
                    />
                  </CellActionRenderSnapshotWrapper>
                ) : (
                  <DocumentActions
                    disableDownload={disableDownloadDocument}
                    onPreview={() => handlePreview(params.data)}
                    onDownload={() => handleDownload(params.data)}
                    onDelete={() => handleDelete(params.data)}
                  />
                )}
              </>
            );
          },
        };
        result.push(newCol);
      } else {
        col = {
          ...col,
          cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        };
        result.push(col);
      }
    });

    result = result.map((colItem) => {
      return {
        cellEditorSelector: () => {},
        ...colItem,
        headerComponent: colItem?.headerName
          ? QaSpecHelpContentGridLabel
          : null,
      };
    });

    return result;
  }, [isToggleSnapshot]);

  return columnDefs;
};

export const generateLocationColumDefs = ({ isToggleSnapshot }) => {
  let columns = [
    {
      allowFilter: true,
      allowSort: true,
      dataType: 'string',
      headerName: 'Location Name',
      field: 'locationName',
      cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
      flex: 1,
      headerComponentParams: {
        fieldPath:
          'qaSpecification.manufacturingProcess.locations.locationName',
      },
    },
    {
      allowFilter: true,
      allowSort: true,
      dataType: 'string',
      headerName: 'Location Description',
      field: 'locationDescription',
      cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
      flex: 1,
      headerComponentParams: {
        fieldPath:
          'qaSpecification.manufacturingProcess.locations.locationDescription',
      },
    },
    {
      allowFilter: true,
      allowSort: true,
      dataType: 'string',
      headerName: 'Main Address',
      field: 'mainAddress',
      cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
      flex: 1,
      headerComponentParams: {
        fieldPath: 'qaSpecification.manufacturingProcess.locations.mainAddress',
      },
    },
    {
      allowFilter: true,
      allowSort: true,
      dataType: 'string',
      headerName: 'City',
      field: 'city',
      cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
      flex: 1,
      headerComponentParams: {
        fieldPath: 'qaSpecification.manufacturingProcess.locations.city',
      },
    },
    {
      allowFilter: true,
      allowSort: true,
      dataType: 'string',
      headerName: 'State',
      field: 'state',
      cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
      flex: 1,
      headerComponentParams: {
        fieldPath: 'qaSpecification.manufacturingProcess.locations.state',
      },
    },
    {
      allowFilter: true,
      allowSort: true,
      dataType: 'string',
      headerName: 'Zip Code',
      field: 'zipcode',
      cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
      flex: 1,
      headerComponentParams: {
        fieldPath: 'qaSpecification.manufacturingProcess.locations.zipcode',
      },
    },
    {
      allowFilter: true,
      allowSort: true,
      dataType: 'boolean',
      headerName: 'Primary Address',
      field: 'isPrimaryAddress',
      cellRenderer: (params) => {
        return (
          <>
            {isToggleSnapshot ? (
              <RadioBooleanSnapshot {...params} />
            ) : (
              <Radio
                style={{ paddingLeft: 16, pointerEvents: 'none' }}
                checked={params?.value}
              />
            )}
          </>
        );
      },
      flex: 1,
      headerComponentParams: {
        fieldPath:
          'qaSpecification.manufacturingProcess.locations.isPrimaryAddress',
      },
    },
  ];

  return columns.map((colItem) => {
    return {
      ...colItem,
      headerComponent: QaSpecHelpContentGridLabel,
    };
  });
};

export const getDisplayNameMaps = (colDefs) => {
  if (!colDefs?.length) return {};

  return colDefs.reduce((accumulator, currentCol) => {
    accumulator[currentCol.field] = currentCol.headerName;
    return accumulator;
  }, {});
};
